import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid payment session');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Please log in to continue');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Check payment status with backend
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setMessage('Payment successful! Your Pro subscription is now active.');
        setPaymentDetails(response.data);
        
        // Update user data in localStorage to reflect new subscription
        const updatedUser = {
          ...user,
          subscription_status: 'pro',
          subscription_plan: 'pro'
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Redirect to home after 3 seconds
        setTimeout(() => navigate('/', { replace: true }), 3000);
      } else {
        setStatus('error');
        setMessage('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      
      if (error.response?.status === 401) {
        setMessage('Session expired. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMessage(error.response?.data?.detail || 'Failed to verify payment. Please contact support.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          {/* Status Icon */}
          <div className="mb-6">
            {status === 'checking' && (
              <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            )}
            
            {status === 'success' && (
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            
            {status === 'error' && (
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {status === 'checking' && 'Verifying Payment...'}
            {status === 'success' && 'Payment Successful! 🎉'}
            {status === 'error' && 'Payment Issue'}
          </h1>

          {/* Message */}
          <p className={`mb-6 ${
            status === 'success' ? 'text-green-600' : 
            status === 'error' ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {message || 'Please wait while we verify your payment...'}
          </p>

          {/* Payment Details */}
          {status === 'success' && paymentDetails && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Plan:</span>
                  <span className="text-gray-900 font-bold">Pro Subscription</span>
                </div>
                {paymentDetails.amount_total && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Amount:</span>
                    <span className="text-gray-900 font-bold">
                      ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Status:</span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                    ✓ Active
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Success Features */}
          {status === 'success' && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-900 mb-3">You now have access to:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Unlimited contacts
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  10 GB storage
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Advanced task management
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Priority support
                </li>
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          {status === 'success' && (
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105"
            >
              Go to Dashboard
            </button>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/payment', { replace: true })}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-300 transition-all duration-200"
              >
                Go to Dashboard
              </button>
            </div>
          )}

          {/* Auto-redirect message */}
          {status === 'success' && (
            <p className="text-sm text-gray-500 mt-4">
              Redirecting to dashboard in 3 seconds...
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
